/**
 * Which Kit bubble CTA / pronoun to use - not a raw "selected" or "pending" count.
 *
 * Derived from how many formal suggestions are available *and* whether they are all batch-selected:
 * - `All`: Quote batch - every pending Quick Quote is selected together ("them all")
 * - `Single`: only one formal suggestion is available ("it" / "select it")
 * - `Multiple`: more than one is available; one is focused or the user picks ("one" / "select one")
 */
export enum KitSelectionVariant {
  All = 'all',
  // Every LTL shipment from one email is set up in the Batch LTL panel.
  BatchLTL = 'batch_ltl',
  // The Batch LTL panel already replied for this email.
  BatchLTLReplied = 'batch_ltl_replied',
  Single = 'single',
  Multiple = 'multiple',
}

// Join "I found ..." fragments with commas and a final "and".
export function joinKitFoundParts(parts: string[]): string {
  switch (parts.length) {
    case 0:
      return '';
    case 1:
      return parts[0];
    case 2:
      return `${parts[0]} and ${parts[1]}`;
    default:
      return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
  }
}

/**
 * Which Quote formal-message trailing phrase to use, based on auto-select shape.
 *
 * - `All`: batch quote on, >=2 pending Quick Quotes, and batch ids cover those pending Quick Quotes
 * - `Single`: exactly one pending formal suggestion (Quick Quote + Load Building)
 * - `Multiple`: multiple formal suggestions, only one selected (default)
 */
export function getKitQuoteSelectionVariant({
  pendingQuickQuoteCount,
  pendingLoadBuildingCount,
  isBatchQuoteEnabled,
  batchSelectedCount,
  batchLTLShipmentCount = 0,
  hasBatchLTLReply = false,
}: {
  pendingQuickQuoteCount: number;
  pendingLoadBuildingCount: number;
  isBatchQuoteEnabled: boolean;
  batchSelectedCount: number;
  // Pending LTL shipments from one email that open in the Batch LTL panel.
  batchLTLShipmentCount?: number;
  hasBatchLTLReply?: boolean;
}): KitSelectionVariant {
  const pendingFormalCount = pendingQuickQuoteCount + pendingLoadBuildingCount;

  if (hasBatchLTLReply) {
    return KitSelectionVariant.BatchLTLReplied;
  }

  if (
    batchLTLShipmentCount >= 2 &&
    batchSelectedCount >= batchLTLShipmentCount
  ) {
    return KitSelectionVariant.BatchLTL;
  }

  const isAllSelected =
    isBatchQuoteEnabled &&
    pendingQuickQuoteCount >= 2 &&
    batchSelectedCount >= pendingQuickQuoteCount;

  if (isAllSelected) {
    return KitSelectionVariant.All;
  }
  if (pendingFormalCount <= 1) {
    return KitSelectionVariant.Single;
  }
  return KitSelectionVariant.Multiple;
}

// Trailing copy after "Hey! I found {parts} and already selected ...".
export function getKitQuoteSelectionTrailingCopy(
  variant: KitSelectionVariant
): string {
  switch (variant) {
    case KitSelectionVariant.All:
    case KitSelectionVariant.BatchLTL:
    case KitSelectionVariant.BatchLTLReplied:
      return 'them all for you as a batch - edit or remove items if needed!';
    case KitSelectionVariant.Single:
      return 'it for you - edit if needed!';
    case KitSelectionVariant.Multiple:
      return 'one for you - edit or swap if needed!';
  }
}

/**
 * Load view has no auto-select - only singular vs plural CTA after
 * "Hey! I found {parts} for this load".
 */
export function getKitLoadSelectionVariant(
  pendingFormalCount: number
): KitSelectionVariant {
  if (pendingFormalCount <= 1) {
    return KitSelectionVariant.Single;
  }
  return KitSelectionVariant.Multiple;
}

// Full Load formal bubble sentence after the found-parts list is joined.
export function getKitLoadFormalMessage(
  foundList: string,
  variant: KitSelectionVariant
): string {
  switch (variant) {
    case KitSelectionVariant.Single:
      return `Hey! I found ${foundList} for this load - select it to get started!`;
    case KitSelectionVariant.All:
    case KitSelectionVariant.BatchLTL:
    case KitSelectionVariant.BatchLTLReplied:
    case KitSelectionVariant.Multiple:
      return `Hey! I found ${foundList} for this load - select one to get started!`;
  }
}

// Kit bubble when an email's LTL shipments are set up in the Batch LTL panel.
export function getKitBatchLTLMessage(shipmentCount: number): string {
  return `I found ${shipmentCount} LTL shipments in this email and set them up as a batch below. Fix anything in red, then get rates.`;
}
